import { useAuth0 } from "@auth0/auth0-react";
import { ChevronLeftIcon, PencilIcon } from "@heroicons/react/24/solid";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Headline, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import {
  CareAppointmentType,
  useCareAppointmentsByResourceEmailQuery,
  useCareResourceByEmailQuery,
} from "@api/graphql/types-and-hooks";
import { EditAppointmentNotesDialog } from "@components/EditAppointmentNotesDialog";
import { OnSchedResourcePill } from "@components/OnSchedResourcePill";
import { WarningCard } from "@components/WarningCard/WarningCard";

export const SchedulingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const userEmail = user?.email;

  // State for notes editing dialog
  const [editingAppointment, setEditingAppointment] = useState<{
    id: string;
    notes: string;
  } | null>(null);

  // Query 1: Check if provider exists in OnSched
  const { data: resourceData, isLoading: isResourceLoading } = useCareResourceByEmailQuery(
    { email: userEmail! },
    {
      enabled: !!userEmail,
      retry: false,
    },
  );

  const onschedResource = resourceData?.careResourceByEmail;
  const resourceExists = !!onschedResource;

  // Query 2: Get appointments (only if resource exists)
  const {
    data: appointmentsData,
    isLoading: isAppointmentsLoading,
    error: appointmentsError,
    refetch: refetchAppointments,
  } = useCareAppointmentsByResourceEmailQuery(
    { email: userEmail! },
    {
      enabled: !!userEmail && resourceExists,
      retry: false,
    },
  );

  const appointments = appointmentsData?.careAppointmentsByResourceEmail || [];

  // Handler for successful notes update
  const handleNotesUpdateSuccess = () => {
    refetchAppointments();
  };

  // Loading state
  if (isResourceLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-row gap-2 items-center cursor-pointer" onClick={() => navigate("/frontera-clarity")}>
          <ChevronLeftIcon className="text-green-700 w-5 h-5" />
          <Paragraph size="small">Back</Paragraph>
        </div>
        <div className="p-12 text-center bg-white border border-gray-200 rounded-lg">
          <Paragraph>Loading...</Paragraph>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-row gap-2 items-center cursor-pointer" onClick={() => navigate("/frontera-clarity")}>
        <ChevronLeftIcon className="text-green-700 w-5 h-5" />
        <Paragraph size="small">Back</Paragraph>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex justify-between items-start mb-6">
          <Headline size="medium" className="text-2xl font-semibold text-gray-900">
            Scheduling
          </Headline>

          {/* OnSched Resource Pill - self-contained */}
          {resourceExists && <OnSchedResourcePill />}
        </div>

        {/* Warning Banner if provider not in OnSched */}
        {!resourceExists && (
          <WarningCard content="You are not set up as a provider in the scheduling system. Please contact support to get access.">
            <Small className="text-gray-600 mt-2">Email: {userEmail}</Small>
          </WarningCard>
        )}

        {/* Show appointments if resource exists */}
        {resourceExists && (
          <div className="space-y-4">
            <Headline size="medium" className="text-lg font-medium mb-4">
              Your Appointments
            </Headline>

            {isAppointmentsLoading && <Paragraph>Loading appointments...</Paragraph>}

            {appointmentsError ? (
              <Paragraph className="text-red-600">Failed to load appointments. Please try again.</Paragraph>
            ) : null}

            {!isAppointmentsLoading && appointments.length === 0 && (
              <Paragraph className="text-gray-600">No appointments scheduled.</Paragraph>
            )}

            {!isAppointmentsLoading && appointments.length > 0 && (
              <div className="space-y-3">
                {appointments
                  ?.filter((apt) => !!apt)
                  ?.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
                  ?.map((apt) => {
                    const isPast = isAppointmentPast(apt.startTime);
                    const relativeTime = getRelativeTime(apt.startTime);

                    return (
                      <div
                        key={apt?.id}
                        className={`border rounded-lg p-4 ${
                          isPast ? "border-gray-300 bg-gray-50" : "border-green-300 bg-green-50 shadow-xs"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span
                                className={`px-2 py-1 rounded-md text-xs font-semibold ${
                                  isPast ? "bg-gray-200 text-gray-700" : "bg-green-600 text-white"
                                }`}
                              >
                                {isPast ? "PAST" : "UPCOMING"}
                              </span>
                              <Small className={isPast ? "text-gray-500" : "text-green-700"}>{relativeTime}</Small>
                            </div>
                            <Paragraph className="font-medium">{apt.serviceName}</Paragraph>
                            <Small className="text-gray-600">
                              Patient: {apt.customerFirstName} {apt.customerLastName}
                            </Small>
                            <Small className="text-gray-600 block">
                              {new Date(apt.startTime).toLocaleString()} - {new Date(apt.endTime).toLocaleTimeString()}
                            </Small>
                            {apt.appointmentMeetingLink && (
                              <Small className="underline cursor-pointer">
                                <a href={apt.appointmentMeetingLink} target="_blank" rel="noopener noreferrer">
                                  {apt.appointmentMeetingLink}
                                </a>
                              </Small>
                            )}

                            {apt.notes && (
                              <Small className="text-gray-600 mt-2 block">
                                <span className="font-medium">Notes:</span> {apt.notes}
                              </Small>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`px-3 py-1 rounded-full text-sm ${getStatusColor(apt.status)}`}>
                              {getStatusLabel(apt)}
                            </div>
                            <button
                              onClick={() =>
                                setEditingAppointment({
                                  id: apt.id,
                                  notes: apt.notes || "",
                                })
                              }
                              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                              title="Edit notes"
                            >
                              <PencilIcon className="w-4 h-4 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Notes Dialog */}
      {editingAppointment && (
        <EditAppointmentNotesDialog
          open={!!editingAppointment}
          onClose={() => setEditingAppointment(null)}
          appointmentId={editingAppointment.id}
          currentNotes={editingAppointment.notes}
          onSuccess={handleNotesUpdateSuccess}
        />
      )}
    </div>
  );
};

// Helper function to check if appointment is in the past
function isAppointmentPast(startTime: string): boolean {
  return new Date(startTime).getTime() < Date.now();
}

// Helper function to get relative time
function getRelativeTime(startTime: string): string {
  const now = new Date();
  const appointmentDate = new Date(startTime);
  const diffMs = appointmentDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Tomorrow";
  } else if (diffDays === -1) {
    return "Yesterday";
  } else if (diffDays > 0) {
    return `in ${diffDays} days`;
  } else {
    return `${Math.abs(diffDays)} days ago`;
  }
}

// Helper function for status colors
function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "confirmed":
      return "bg-green-100 text-green-800";
    case "scheduled":
      return "bg-blue-100 text-blue-800";
    case "completed":
      return "bg-gray-100 text-gray-800";
    case "cancelled":
      return "bg-red-100 text-red-800";
    case "no-show":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

/**
 * @param apt - The appointment to get the status label for
 * @see https://docs.onsched.com/docs/appointment-status
 */
function getStatusLabel(apt: CareAppointmentType): string {
  switch (apt.status.toLowerCase()) {
    case "bk":
      if (apt.notes?.length) {
        return "Completed";
      }
      return "Booked";
    case "in":
      return "Initial";
    case "rs":
      return "Ready to Schedule";
    case "re":
      return "Rescheduled";
    case "cn":
      return "Cancelled";
    default:
      return apt.status;
  }
}
